import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { formatUsPhone } from "@/lib/format";

export function DealerOverviewTab({ dealer, onSaved }) {
  const [form, setForm] = useState({
    legal_name: dealer.legal_name || "",
    dba: dealer.dba || "",
    website: dealer.website || "",
    notes: dealer.notes || "",
    contact_name: dealer.primary_contact?.name || "",
    contact_title: dealer.primary_contact?.title || "",
    contact_email: dealer.primary_contact?.email || "",
    contact_phone: dealer.primary_contact?.phone || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/dealers/${dealer.id}`, {
        legal_name: form.legal_name,
        dba: form.dba,
        website: form.website,
        notes: form.notes,
        primary_contact: {
          name: form.contact_name,
          title: form.contact_title,
          email: form.contact_email || null,
          phone: form.contact_phone,
        },
      });
      toast.success("Dealer updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-border bg-white shadow-[0_2px_12px_rgba(26,26,24,0.04)]">
      <CardContent className="p-8">
        <form onSubmit={handleSave} className="space-y-8" data-testid="dealer-edit-form">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Legal business name</Label>
              <Input data-testid="edit-legal-name" value={form.legal_name} onChange={set("legal_name")} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">DBA / Showroom name</Label>
              <Input data-testid="edit-dba" value={form.dba} onChange={set("dba")} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Website</Label>
              <Input data-testid="edit-website" value={form.website} onChange={set("website")} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
          </div>

          <div className="border-t border-border pt-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Primary contact</p>
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Name</Label>
                <Input data-testid="edit-contact-name" value={form.contact_name} onChange={set("contact_name")} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Title</Label>
                <Input data-testid="edit-contact-title" value={form.contact_title} onChange={set("contact_title")} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Email</Label>
                <Input type="email" data-testid="edit-contact-email" value={form.contact_email} onChange={set("contact_email")} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Phone</Label>
                <Input data-testid="edit-contact-phone" value={form.contact_phone} onChange={set("contact_phone")} onBlur={() => setForm((f) => ({ ...f, contact_phone: formatUsPhone(f.contact_phone) }))} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Internal notes (admin-only — never shown to dealers)</Label>
            <Textarea data-testid="edit-notes" value={form.notes} onChange={set("notes")} rows={3} className="bg-white focus-visible:ring-terracotta" />
          </div>

          <Button
            type="submit"
            disabled={saving}
            data-testid="save-dealer-button"
            className="h-11 bg-terracotta px-8 text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
